// Anchors for tutorial step targets (#1859). A target outside the paper's
// engine state is named here and marked at its call site with
// `data-tour={TOUR_ANCHORS.x}` (never a string literal: `tourAnchors.test.ts`
// scans for both). Paper blocks are not anchors — they are resolved from engine
// state each frame and found by their lines' `data-line-id`.
//
// A name never holds an id. Rows of a list carry their entity's id in
// `data-tour-key`, and a target names the one it means by key — itself, or
// the row it sits `within`.

import type { BlockId, EngineState, LineId } from '@/editor/engine/types';

export const TOUR_ANCHORS = {
    /** The paper's scroll container: the viewport a paper target must be in. */
    paperScroller: 'paper-scroller',
    /** «Добавить … в персонажей?» under a committed line (core step 4). */
    capsPrompt: 'caps-prompt',
    /** The browse-⌫ «Удалить …?» dialog (Browse chapter, step 3). */
    browseDeletePrompt: 'browse-delete-prompt',
    /** The header's «Навигатор» toggle (Structure chapter, step 1). */
    navigatorToggle: 'navigator-toggle',
    /** Navigator rows, list and table alike, keyed by the entity's id. */
    navAct: 'nav-act',
    navEpisode: 'nav-episode',
    navScene: 'nav-scene',
    /** A scene row's logline field (Structure chapter, step 4). */
    navSceneLogline: 'nav-scene-logline',
    /** The header's notes-sidebar toggle (Notes chapter, step 3). */
    notesToggle: 'notes-toggle',
    /** The notes sidebar's panel (Notes chapter, step 4). */
    notesSidebar: 'notes-sidebar',
    /** The header's visibility-popover trigger (Visibility chapter, steps 1, 2, 5). */
    visibilityToggle: 'visibility-toggle',
    /** The header's «Сгенерировать логлайны и сюжеты» (AI chapter, step 1). */
    generateMenu: 'generate-menu',
} as const;

export type TourAnchor = (typeof TOUR_ANCHORS)[keyof typeof TOUR_ANCHORS];

/** An anchored element: the one keyed `key`, or the one inside the row keyed
 *  `within.key` — or every element so named when neither is given. */
export interface AnchorTarget {
    kind: 'anchor';
    anchor: TourAnchor;
    key?: number;
    within?: { anchor: TourAnchor; key: number };
}

/** What a step points at. The ring defaults to the whole block. */
export type TourTarget = { kind: 'block'; id: BlockId } | { kind: 'line'; id: LineId } | AnchorTarget;

export const anchorSelector = (anchor: TourAnchor, key?: number) =>
    key === undefined ? `[data-tour="${anchor}"]` : `[data-tour="${anchor}"][data-tour-key="${key}"]`;

export const targetSelector = ({ anchor, key, within }: AnchorTarget) =>
    within === undefined ? anchorSelector(anchor, key) : `${anchorSelector(within.anchor, within.key)} ${anchorSelector(anchor, key)}`;

/** The elements a target covers right now: every row of a block, the one
 *  line, or the anchored element. Empty when it is not in the DOM
 *  (virtualized out, not rendered yet).
 *
 *  A block is found through its lines' `data-line-id`, not `data-block-id`:
 *  blockIds are re-minted on every parse, and a memoised row keeps painting
 *  the one it was rendered with. */
export function targetElements(target: TourTarget, state: EngineState, root: ParentNode = document): HTMLElement[] {
    if (target.kind === 'anchor') return [...root.querySelectorAll<HTMLElement>(targetSelector(target))];
    const lineIds = target.kind === 'line' ? [target.id] : (state.flatLinesByBlockId.get(target.id) ?? []).map(line => line.lineId);
    return lineIds.flatMap(lineId => [...root.querySelectorAll<HTMLElement>(`[data-line-id="${lineId}"]`)]);
}
